import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { redditPostsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Edit, Check, X, Loader2,
  Mic, Pause, Play, Image, Video, Download, FileText, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const RedditPostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const mediaInputRef = useRef(null);
  const voiceInputRef = useRef(null);
  const audioRefs = useRef({});

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [postCopy, setPostCopy] = useState('');
  const [savingCopy, setSavingCopy] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [playingNoteId, setPlayingNoteId] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  useEffect(() => { fetchPost(); }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const res = await redditPostsAPI.getById(id);
      setPost(res.data);
      setEditTitle(res.data.title || '');
      setPostCopy(res.data.post_copy || '');
    } catch {
      toast.error('Failed to load post');
      navigate('/ideas/reddit');
    } finally { setLoading(false); }
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim()) return;
    try {
      await redditPostsAPI.update(id, { title: editTitle.trim() });
      toast.success('Title updated');
      setEditing(false);
      fetchPost();
    } catch { toast.error('Failed to update title'); }
  };

  const handleSaveCopy = async () => {
    setSavingCopy(true);
    try {
      await redditPostsAPI.update(id, { post_copy: postCopy });
      toast.success('Post copy saved');
    } catch { toast.error('Failed to save copy'); }
    finally { setSavingCopy(false); }
  };

  // Media
  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingMedia(true);
    let count = 0;
    for (const file of files) {
      try {
        await redditPostsAPI.uploadMedia(id, file);
        count++;
      } catch { /* skip failed */ }
    }
    if (count) toast.success(`${count} file${count > 1 ? 's' : ''} uploaded`);
    setUploadingMedia(false);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
    fetchPost();
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await redditPostsAPI.deleteMedia(id, mediaId);
      toast.success('Media deleted');
      fetchPost();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownloadMedia = (mediaId, originalName) => {
    const url = redditPostsAPI.getMediaDownloadUrl(id, mediaId);
    const a = document.createElement('a');
    a.href = url;
    a.download = originalName || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Voice Notes
  const handleVoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVoice(true);
    try {
      await redditPostsAPI.uploadVoiceNote(id, file);
      toast.success('Voice note uploaded');
      fetchPost();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally {
      setUploadingVoice(false);
      if (voiceInputRef.current) voiceInputRef.current.value = '';
    }
  };

  const handleDeleteVoiceNote = async (noteId) => {
    try {
      await redditPostsAPI.deleteVoiceNote(id, noteId);
      toast.success('Voice note deleted');
      fetchPost();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePlay = (noteId) => {
    const audio = audioRefs.current[noteId];
    if (!audio) return;
    if (playingNoteId === noteId) {
      audio.pause();
      setPlayingNoteId(null);
    } else {
      Object.values(audioRefs.current).forEach(a => a?.pause());
      audio.play();
      setPlayingNoteId(noteId);
    }
  };

  if (loading || !post) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="reddit-post-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/ideas/reddit')} className="mt-1 text-zinc-400 hover:text-zinc-700">
            <ArrowLeft size={20} />
          </button>
          <div>
            {editing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-xl font-semibold w-80"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
                  data-testid="edit-title-input"
                />
                <Button size="sm" onClick={handleSaveTitle} className="bg-zinc-900 text-white">
                  <Check size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditTitle(post.title); }}>
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">{post.title}</h1>
            )}
            <p className="text-xs text-zinc-400 mt-1">
              by {post.created_by_name} &middot; {new Date(post.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-title-btn">
            <Edit size={14} className="mr-1.5" /> Edit Title
          </Button>
        )}
      </div>

      {/* Voice Notes */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="voice-notes-section">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mic className="w-5 h-5 text-zinc-600" />
            <div>
              <h3 className="font-medium text-zinc-900">Voice Notes</h3>
              <p className="text-xs text-zinc-500">Record context and ideas for this post</p>
            </div>
          </div>
          <div>
            <input ref={voiceInputRef} type="file" accept="audio/*" onChange={handleVoiceUpload} className="hidden" />
            <Button size="sm" variant="outline" onClick={() => voiceInputRef.current?.click()} disabled={uploadingVoice} data-testid="upload-voice-note-btn">
              {uploadingVoice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Voice Note
            </Button>
          </div>
        </div>
        <div className="p-6">
          {(!post.voice_notes || post.voice_notes.length === 0) ? (
            <div className="text-center py-6 text-zinc-400 text-sm">
              <Mic className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No voice notes yet
            </div>
          ) : (
            <div className="space-y-3">
              {post.voice_notes.map((note) => {
                const streamUrl = redditPostsAPI.getVoiceNoteStreamUrl(id, note.id);
                const isPlaying = playingNoteId === note.id;
                return (
                  <div key={note.id} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg border border-zinc-100" data-testid={`voice-note-${note.id}`}>
                    <button
                      onClick={() => togglePlay(note.id)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        isPlaying ? 'bg-orange-500 text-white' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'
                      }`}
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <audio
                      ref={(el) => { audioRefs.current[note.id] = el; }}
                      src={streamUrl}
                      onEnded={() => setPlayingNoteId(null)}
                      preload="none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{note.original_name || 'Voice Note'}</p>
                      <p className="text-xs text-zinc-400">
                        {note.recorder_name && `${note.recorder_name} · `}
                        {note.file_size ? `${(note.file_size / 1024).toFixed(0)} KB` : ''}
                      </p>
                    </div>
                    <button onClick={() => handleDeleteVoiceNote(note.id)} className="text-zinc-400 hover:text-red-500 p-1" data-testid={`delete-voice-${note.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Post Copy */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="post-copy-section">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-zinc-600" />
            <div>
              <h3 className="font-medium text-zinc-900">Post Copy</h3>
              <p className="text-xs text-zinc-500">Write the Reddit post text</p>
            </div>
          </div>
          <Button size="sm" onClick={handleSaveCopy} disabled={savingCopy} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-copy-btn">
            {savingCopy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Copy
          </Button>
        </div>
        <div className="p-6">
          <Textarea
            value={postCopy}
            onChange={(e) => setPostCopy(e.target.value)}
            placeholder="Write your Reddit post copy here..."
            rows={8}
            className="w-full resize-none focus:ring-2 focus:ring-zinc-200"
            data-testid="post-copy-textarea"
          />
        </div>
      </div>

      {/* Media */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="media-section">
        <div className="px-6 py-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image className="w-5 h-5 text-zinc-600" />
            <div>
              <h3 className="font-medium text-zinc-900">Media</h3>
              <p className="text-xs text-zinc-500">Attach images and videos for this post</p>
            </div>
          </div>
          <div>
            <input ref={mediaInputRef} type="file" multiple accept="image/*,video/*" onChange={handleMediaUpload} className="hidden" />
            <Button size="sm" variant="outline" onClick={() => mediaInputRef.current?.click()} disabled={uploadingMedia} data-testid="upload-media-btn">
              {uploadingMedia ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Media
            </Button>
          </div>
        </div>
        <div className="p-6">
          {(!post.media || post.media.length === 0) ? (
            <div className="text-center py-8 text-zinc-400 text-sm">
              <Image className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No media attached yet
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {post.media.map((item) => {
                const fileUrl = redditPostsAPI.getMediaUrl(id, item.id);
                return (
                  <div key={item.id} className="relative group rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50" data-testid={`media-item-${item.id}`}>
                    <div className="aspect-square">
                      {item.is_video ? (
                        <video src={fileUrl} className="w-full h-full object-cover" muted playsInline />
                      ) : (
                        <img src={fileUrl} alt={item.original_name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                      {item.is_video && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                            <Play size={18} className="text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Overlay actions */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                      <button
                        onClick={() => setPreviewMedia(item)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-zinc-700 hover:bg-white"
                        title="Preview"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleDownloadMedia(item.id, item.original_name)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-zinc-700 hover:bg-white"
                        title="Download"
                        data-testid={`download-media-${item.id}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteMedia(item.id)}
                        className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-red-500 hover:bg-white"
                        title="Delete"
                        data-testid={`delete-media-${item.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="px-2 py-1.5 bg-white border-t border-zinc-100">
                      <p className="text-xs text-zinc-600 truncate">{item.original_name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Media Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium text-zinc-700">{previewMedia?.original_name}</DialogTitle>
          </DialogHeader>
          {previewMedia && (
            <div className="flex items-center justify-center max-h-[70vh] overflow-auto">
              {previewMedia.is_video ? (
                <video
                  src={redditPostsAPI.getMediaUrl(id, previewMedia.id)}
                  controls autoPlay
                  className="max-w-full max-h-[65vh] rounded"
                />
              ) : (
                <img
                  src={redditPostsAPI.getMediaUrl(id, previewMedia.id)}
                  alt={previewMedia.original_name}
                  className="max-w-full max-h-[65vh] rounded object-contain"
                />
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline" size="sm"
              onClick={() => previewMedia && handleDownloadMedia(previewMedia.id, previewMedia.original_name)}
            >
              <Download size={14} className="mr-1.5" /> Download
            </Button>
            <Button
              variant="outline" size="sm"
              className="text-red-500 border-red-200 hover:bg-red-50"
              onClick={() => {
                if (previewMedia) {
                  handleDeleteMedia(previewMedia.id);
                  setPreviewMedia(null);
                }
              }}
            >
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedditPostDetail;
